import { Headphones, HeadphoneOff, Maximize, Mic, MicOff, Minimize, PhoneCall, PhoneOff, ScreenShare, ScreenShareOff, Settings, Video, VideoOff, VolumeX } from '@/components/ui/icons';
import { VoiceUserMenu } from './VoiceUserMenu';
import { useEffect, useRef, useState } from 'react';
import type { ChannelDTO, VoiceParticipantDTO } from '@clanfire/shared';
import { Illustration } from '@/components/ui/Illustration';
import { Avatar } from '@/components/ui/Avatar';
import { Button } from '@/components/ui/Button';
import { useChannelPermissions } from '@/lib/permissions';
import { cn } from '@/lib/utils';
import { screenShareSupported, voiceManager } from '@/realtime/voice';
import { useAuth } from '@/stores/auth';
import { useVoice } from '@/stores/realtime';
import { useUi } from '@/stores/ui';
import { useMemberName, useServerLook } from '@/stores/servers';
import { useUser } from '@/stores/users';
import { useVoiceSettings } from '@/stores/voiceSettings';

const EMPTY: VoiceParticipantDTO[] = [];

export function VoiceChannelView({ channel }: { channel: ChannelDTO }) {
  const participants = useVoice((s) => s.participants[channel.id] ?? EMPTY);
  const connectedHere = useVoice((s) => s.channelId === channel.id);
  const status = useVoice((s) => s.status);
  const muted = useVoice((s) => s.muted);
  const deafened = useVoice((s) => s.deafened);
  const video = useVoice((s) => s.video);
  const screen = useVoice((s) => s.screen);
  const micUnavailable = useVoice((s) => s.micUnavailable);
  const openModal = useUi((s) => s.openModal);
  const perms = useChannelPermissions(channel.id);
  const canConnect = perms.can('CONNECT');
  const sharers = participants.filter((p) => p.screen);
  const anyVideo = participants.some((p) => p.video) || sharers.length > 0;

  return (
    <section aria-label={`Voice channel ${channel.name}`} className="relative flex min-w-0 flex-1 flex-col bg-rail">
      <div aria-hidden className="pointer-events-none absolute inset-0 opacity-40" style={{ background: 'radial-gradient(ellipse at 50% 120%, var(--clanfire-accent-soft), transparent 60%)' }} />
      <div className="relative flex min-h-0 flex-1 items-center justify-center overflow-y-auto p-4 sm:p-6">
        {participants.length === 0 ? (
          <div className="text-center">
            <Illustration name="voice" className="mx-auto" />
            <h2 className="mt-4 text-2xl font-extrabold tracking-tight text-fg">{channel.name}</h2>
            <p className="mt-1 text-fg-muted">Nobody's in this channel yet.</p>
          </div>
        ) : (
          <div className="flex w-full flex-col items-center gap-4">
            {sharers.length > 0 && (
              <ul aria-label="Screen shares" className="grid w-full max-w-6xl grid-cols-[repeat(auto-fit,minmax(min(100%,420px),1fr))] gap-4">
                {sharers.map((p) => (
                  <ScreenTile key={p.userId} participant={p} />
                ))}
              </ul>
            )}
            <ul
              className={cn(
                'grid w-full justify-center gap-4',
                sharers.length > 0
                  ? 'max-w-6xl grid-cols-[repeat(auto-fit,minmax(140px,180px))]'
                  : anyVideo
                    ? 'max-w-6xl grid-cols-[repeat(auto-fit,minmax(min(100%,280px),1fr))]'
                    : 'max-w-4xl grid-cols-[repeat(auto-fit,minmax(160px,220px))]',
              )}
            >
              {participants.map((p) => (
                <ParticipantTile key={p.userId} participant={p} wide={anyVideo && sharers.length === 0} />
              ))}
            </ul>
          </div>
        )}
      </div>

      <div className="relative flex shrink-0 flex-col items-center gap-2 border-t border-line bg-sidebar/80 px-4 py-4 backdrop-blur">
        {micUnavailable && connectedHere && <p className="text-xs text-warning">Microphone unavailable — you're listening only. Check Voice & Video settings, then rejoin.</p>}
        {connectedHere && status === 'connecting' && <p className="text-xs text-fg-muted">Connecting…</p>}
        {connectedHere ? (
          <div className="flex flex-wrap items-center justify-center gap-3">
            <ControlButton label={muted ? 'Unmute' : 'Mute'} active={muted} onClick={() => voiceManager.setMuted(!muted)} disabled={micUnavailable}>
              {muted ? <MicOff size={22} /> : <Mic size={22} />}
            </ControlButton>
            <ControlButton label={deafened ? 'Undeafen' : 'Deafen'} active={deafened} onClick={() => voiceManager.setDeafened(!deafened)}>
              {deafened ? <HeadphoneOff size={22} /> : <Headphones size={22} />}
            </ControlButton>
            <ControlButton label={video ? 'Turn off camera' : 'Turn on camera'} active={false} on={video} onClick={() => void voiceManager.setVideo(!video)} disabled={status !== 'connected'}>
              {video ? <Video size={22} /> : <VideoOff size={22} />}
            </ControlButton>
            {screenShareSupported() && (
              <ControlButton label={screen ? 'Stop sharing' : 'Share your screen'} active={false} on={screen} onClick={() => void voiceManager.setScreenShare(!screen)} disabled={status !== 'connected'}>
                {screen ? <ScreenShareOff size={22} /> : <ScreenShare size={22} />}
              </ControlButton>
            )}
            <ControlButton label="Voice & video settings" active={false} onClick={() => openModal({ type: 'userSettings', section: 'voice' })}>
              <Settings size={22} />
            </ControlButton>
            <Button variant="danger" size="lg" icon={<PhoneOff size={20} />} onClick={() => voiceManager.leave()} className="rounded-full!">
              Leave
            </Button>
          </div>
        ) : (
          <>
            <Button
              size="lg"
              icon={<PhoneCall size={20} />}
              disabled={!canConnect || status === 'connecting'}
              onClick={() => void voiceManager.join(channel.id, channel.serverId)}
              className="rounded-full! px-8"
            >
              Join voice
            </Button>
            <p className="text-xs text-fg-muted">
              {canConnect
                ? participants.length > 0
                  ? `${participants.length} ${participants.length === 1 ? 'person is' : 'people are'} here.`
                  : 'Voice is peer-to-peer (WebRTC) and works best with small groups.'
                : "You don't have permission to join this channel."}
            </p>
          </>
        )}
      </div>
    </section>
  );
}

function ControlButton({
  label,
  active,
  on,
  onClick,
  children,
  disabled,
}: {
  label: string;
  /** "Bad" state (muted/deafened): red. */
  active: boolean;
  /** "Good" toggle state (camera on): highlighted. */
  on?: boolean;
  onClick: () => void;
  children: React.ReactNode;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      aria-pressed={on ?? active}
      title={label}
      disabled={disabled}
      className={cn(
        'flex h-12 w-12 items-center justify-center rounded-full transition-colors disabled:opacity-40',
        active ? 'bg-danger text-white hover:brightness-110' : on ? 'bg-fg text-surface hover:brightness-90' : 'bg-elevated text-fg hover:bg-active',
      )}
    >
      {children}
    </button>
  );
}

function VideoStream({ stream, mirror, fit = 'cover' }: { stream: MediaStream; mirror: boolean; fit?: 'cover' | 'contain' }) {
  const ref = useRef<HTMLVideoElement>(null);
  useEffect(() => {
    if (ref.current) ref.current.srcObject = stream;
  }, [stream]);
  return <video ref={ref} autoPlay playsInline muted className={cn('h-full w-full bg-black', fit === 'cover' ? 'object-cover' : 'object-contain', mirror && '-scale-x-100')} />;
}

/** Whether `el` is currently the browser's full-screen element. */
function useIsFullscreen(ref: React.RefObject<HTMLElement | null>) {
  const [on, setOn] = useState(false);
  useEffect(() => {
    const update = () => setOn(!!ref.current && document.fullscreenElement === ref.current);
    document.addEventListener('fullscreenchange', update);
    return () => document.removeEventListener('fullscreenchange', update);
  }, [ref]);
  return on;
}

function ScreenTile({ participant }: { participant: VoiceParticipantDTO }) {
  const user = useUser(participant.userId);
  const isMe = useAuth((s) => s.user?.id === participant.userId);
  const stream = useVoice((s) => (isMe ? s.localScreen : (s.remoteScreen[participant.userId] ?? null)));
  const sharingWholeScreen = useVoice((s) => isMe && s.screenSurface === 'monitor');
  const stageRef = useRef<HTMLDivElement>(null);
  const fullscreen = useIsFullscreen(stageRef);
  // In-app fallback where element full screen isn't available (e.g. iPhone) or is refused.
  const [expanded, setExpanded] = useState(false);
  // Watching your own *entire* screen creates an infinite mirror, so that preview starts hidden.
  const [showOwnPreview, setShowOwnPreview] = useState(false);
  const sharer = useMemberName(participant.serverId, participant.userId) ?? user?.displayName ?? '…';
  const name = isMe ? 'Your screen' : `${sharer}'s screen`;
  const big = fullscreen || expanded;
  const previewHidden = sharingWholeScreen && !showOwnPreview;

  useEffect(() => {
    if (!expanded) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setExpanded(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [expanded]);

  const toggle = () => {
    if (fullscreen) return void document.exitFullscreen().catch(() => undefined);
    if (expanded) return setExpanded(false);
    const stage = stageRef.current;
    if (stage && document.fullscreenEnabled) {
      stage.requestFullscreen({ navigationUI: 'hide' }).catch(() => setExpanded(true));
    } else setExpanded(true);
  };

  return (
    <li className="aspect-video">
      <div
        ref={stageRef}
        onDoubleClick={toggle}
        className={cn(
          'group overflow-hidden bg-black',
          // Never combine "relative" with "fixed": the generated CSS order would let "relative" win.
          expanded ? 'fixed inset-0 z-50' : 'relative h-full w-full',
          big ? 'rounded-none border-0' : 'rounded-2xl border-2 border-line',
        )}
      >
        {previewHidden ? (
          <div className="flex h-full flex-col items-center justify-center gap-3 p-4 text-center text-sm text-white/80">
            <ScreenShare size={32} className="text-white/60" />
            <p>You're sharing your entire screen. Your preview is hidden so it doesn't show itself over and over.</p>
            <Button size="sm" variant="secondary" onClick={() => setShowOwnPreview(true)}>
              Show preview
            </Button>
          </div>
        ) : stream ? (
          <VideoStream stream={stream} mirror={false} fit="contain" />
        ) : (
          <div className="flex h-full items-center justify-center text-sm text-white/70">Connecting to stream…</div>
        )}
        <span className="absolute top-2 left-2 rounded-md bg-danger px-1.5 py-0.5 text-[11px] font-extrabold tracking-wide text-white">LIVE</span>
        <span className="absolute bottom-2 left-2 flex max-w-[calc(100%-4rem)] items-center gap-1.5 rounded-lg bg-black/60 px-2 py-1 text-sm font-semibold text-white">
          <ScreenShare size={14} className="shrink-0" />
          <span className="truncate">{name}</span>
        </span>
        <button
          type="button"
          aria-label={big ? 'Exit full screen' : `View ${name} full screen`}
          title={big ? 'Exit full screen (Esc)' : 'Full screen (or double-click)'}
          onClick={toggle}
          className={cn(
            'absolute right-2 bottom-2 rounded-lg bg-black/60 p-1.5 text-white transition-opacity focus-visible:opacity-100',
            big ? 'opacity-60 hover:opacity-100' : 'opacity-0 group-hover:opacity-100',
          )}
        >
          {big ? <Minimize size={16} /> : <Maximize size={16} />}
        </button>
      </div>
    </li>
  );
}

function ParticipantTile({ participant, wide }: { participant: VoiceParticipantDTO; wide: boolean }) {
  const user = useServerLook(participant.serverId, useUser(participant.userId));
  const isMe = useAuth((s) => s.user?.id === participant.userId);
  const stream = useVoice((s) => (isMe ? s.localVideo : (s.remoteVideo[participant.userId] ?? null)));
  const mirror = useVoiceSettings((s) => s.mirrorVideo);
  const speaking = useVoice((s) => !!s.speaking[participant.userId]) && !participant.muted;
  const openModal = useUi((s) => s.openModal);
  const locallyMuted = useVoiceSettings((s) => !!s.localMutes[participant.userId]);
  const [menu, setMenu] = useState<{ x: number; y: number } | null>(null);
  const name = useMemberName(participant.serverId, participant.userId) ?? user?.displayName ?? '…';
  const showVideo = participant.video && !!stream;

  const status = locallyMuted ? (
    <VolumeX size={15} className="shrink-0 text-fg-muted" aria-label="Muted for you" />
  ) : participant.deafened ? (
    <HeadphoneOff size={15} className="shrink-0 text-danger" aria-label="Deafened" />
  ) : participant.muted ? (
    <MicOff size={15} className="shrink-0 text-danger" aria-label="Muted" />
  ) : null;

  return (
    <li>
      <button
        type="button"
        onClick={() => openModal({ type: 'profile', userId: participant.userId, serverId: participant.serverId })}
        onContextMenu={(e) => {
          e.preventDefault();
          setMenu({ x: e.clientX, y: e.clientY });
        }}
        className={cn(
          'relative flex w-full flex-col items-center justify-center gap-3 overflow-hidden rounded-2xl border-2 bg-elevated transition-all',
          wide ? 'aspect-video' : 'aspect-[4/3]',
          speaking ? 'border-online shadow-[0_0_0_4px_rgba(62,207,142,0.2)]' : 'border-transparent hover:border-line-strong',
        )}
      >
        {showVideo ? (
          <>
            <VideoStream stream={stream} mirror={isMe && mirror} />
            <span className="absolute bottom-2 left-2 flex max-w-[calc(100%-1rem)] items-center gap-1.5 rounded-lg bg-black/60 px-2 py-1 text-sm font-semibold text-white">
              <span className="truncate">{name}</span>
              {status}
            </span>
          </>
        ) : (
          <>
            <Avatar user={user} size={wide ? 80 : 72} speaking={speaking} />
            <span className="flex max-w-full items-center gap-1.5 px-2">
              <span className="truncate font-semibold text-fg">{name}</span>
              {status}
            </span>
          </>
        )}
      </button>
      {menu && <VoiceUserMenu userId={participant.userId} serverId={participant.serverId} point={menu} onClose={() => setMenu(null)} />}
    </li>
  );
}
